import {
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';

import { useFeedbackTerminology } from '@/entities/workspace';
import type { DashboardDateFilterState } from '@/features/Dashboard/components/DashboardDateFilter';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';

import { useFeedbackGalleryData } from '../hooks';
import { getFeedbackGalleryTitle } from '../model/constants';
import {
  canLoadNextFeedbackGalleryPage,
  isFeedbackGalleryNearBottom,
  shouldAutoFillFeedbackGalleryFallback,
} from '../model/scroll-fallback';
import type { FeedbacksSettingT } from '../model/types';
import FeedbackGalleryGrid from './feedback-gallery-grid';
import FeedbackGalleryHeader from './feedback-gallery-header';

const SCROLL_BOTTOM_THRESHOLD = 160;

type FeedbackGalleryViewProps = {
  feedbacksSetting: FeedbacksSettingT;
  columnId: string;
  queryEnabled?: boolean;
  isPublic?: boolean;
  workspaceId?: string;
  disableCardClick?: boolean;
  globalFilter?: StructuredFilter | null;
  globalDateFilter?: DashboardDateFilterState | null;
  showHeader?: boolean;
  headerAction?: ReactNode;
  onTitleClick?: () => void;
};

const FeedbackGalleryView = ({
  feedbacksSetting,
  columnId,
  queryEnabled = true,
  isPublic,
  workspaceId: workspaceIdProp,
  disableCardClick = false,
  globalFilter,
  globalDateFilter,
  showHeader = true,
  headerAction,
  onTitleClick,
}: FeedbackGalleryViewProps) => {
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const { workspace_id: routeWorkspaceId } = useParams();
  const workspaceId = workspaceIdProp ?? routeWorkspaceId;
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const fallbackAutoFillAttemptedRef = useRef(false);
  const supportsIntersectionObserver =
    typeof IntersectionObserver !== 'undefined';

  const globalDateRange = useMemo(() => {
    if (!globalDateFilter?.dateRange) return null;
    return [
      globalDateFilter.dateRange[0].toJSON(),
      globalDateFilter.dateRange[1].toJSON(),
    ] as const;
  }, [globalDateFilter]);
  const queryIdentity = useMemo(
    () =>
      JSON.stringify({
        columnId,
        filterSet: feedbacksSetting.filter_set,
        sortBy: feedbacksSetting.sort_by,
        orderBy: feedbacksSetting.order_by,
        workspaceId,
        globalFilter: globalFilter || null,
        globalDateRange,
        isPublic: isPublic ?? false,
      }),
    [
      columnId,
      feedbacksSetting.filter_set,
      feedbacksSetting.order_by,
      feedbacksSetting.sort_by,
      globalDateRange,
      globalFilter,
      isPublic,
      workspaceId,
    ],
  );

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFeedbackGalleryData({
    columnId,
    filterSet: feedbacksSetting.filter_set,
    sortBy: feedbacksSetting.sort_by,
    orderBy: feedbacksSetting.order_by,
    enabled: queryEnabled,
    isPublic,
    workspaceId,
    globalFilter,
    globalDateFilter,
  });

  const pages = data?.pages;
  const loadedFeedbacks = useMemo(() => {
    return pages?.flatMap((page) => page.feedbacks ?? []) ?? [];
  }, [pages]);
  const totalFeedbackCount = pages?.[0]?.count ?? 0;
  const isInitialLoading = isLoading && loadedFeedbacks.length === 0;
  const isUpdating = isFetchingNextPage;
  const isDateOverridden = Boolean(globalDateRange);
  const startDate =
    globalDateRange?.[0] ??
    (feedbacksSetting.filter_set?.[FEEDBACK_COLUMNS.DATE_START] as
      | string
      | undefined);
  const endDate =
    globalDateRange?.[1] ??
    (feedbacksSetting.filter_set?.[FEEDBACK_COLUMNS.DATE_END] as
      | string
      | undefined);

  const loadNextPage = useCallback(() => {
    if (!queryEnabled) return;
    if (
      !canLoadNextFeedbackGalleryPage({
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
      })
    ) {
      return;
    }
    void fetchNextPage();
  }, [
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    queryEnabled,
  ]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  useEffect(() => {
    fallbackAutoFillAttemptedRef.current = false;
  }, [queryIdentity]);

  const setLoadMoreTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;

      if (!supportsIntersectionObserver) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;
          loadNextPage();
        },
        {
          root: scrollRef.current,
          rootMargin: `${SCROLL_BOTTOM_THRESHOLD}px 0px`,
        },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [loadNextPage, supportsIntersectionObserver],
  );

  const handleFallbackScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (supportsIntersectionObserver) return;

      if (
        !isFeedbackGalleryNearBottom({
          scrollTop: event.currentTarget.scrollTop,
          clientHeight: event.currentTarget.clientHeight,
          scrollHeight: event.currentTarget.scrollHeight,
          threshold: SCROLL_BOTTOM_THRESHOLD,
        })
      ) {
        return;
      }

      loadNextPage();
    },
    [loadNextPage, supportsIntersectionObserver],
  );

  useEffect(() => {
    const target = scrollRef.current;
    if (!target) return;

    const isScrollable = target.scrollHeight > target.clientHeight + 1;
    if (
      !shouldAutoFillFeedbackGalleryFallback({
        isIntersectionObserverSupported: supportsIntersectionObserver,
        hasAutoFillAttempted: fallbackAutoFillAttemptedRef.current,
        isInitialLoading,
        isScrollable,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
      })
    ) {
      return;
    }

    fallbackAutoFillAttemptedRef.current = true;
    loadNextPage();
  }, [
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isInitialLoading,
    isLoading,
    loadNextPage,
    loadedFeedbacks.length,
    supportsIntersectionObserver,
  ]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {showHeader && (
        <FeedbackGalleryHeader
          title={getFeedbackGalleryTitle(
            feedbacksSetting.title,
            replaceFeedbackTerminology,
          )}
          sortBy={feedbacksSetting.sort_by}
          orderBy={feedbacksSetting.order_by}
          startDate={startDate}
          endDate={endDate}
          isDateOverridden={isDateOverridden}
          count={totalFeedbackCount}
          isLoading={isLoading || isFetching}
          rightAction={headerAction}
          onTitleClick={onTitleClick}
        />
      )}
      <div
        ref={scrollRef}
        className="relative min-h-0 flex-1 overflow-y-auto px-7 py-6 [scrollbar-gutter:stable]"
        onScroll={
          supportsIntersectionObserver ? undefined : handleFallbackScroll
        }
      >
        {isInitialLoading ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <span className="text-sm text-gray-400">Loading...</span>
          </div>
        ) : (
          <FeedbackGalleryGrid
            feedbacks={loadedFeedbacks}
            disableCardClick={disableCardClick}
          />
        )}
        {isUpdating && loadedFeedbacks.length > 0 && (
          <div className="flex justify-center py-4">
            <span className="rounded-full border border-gray-200 bg-white/90 px-3 py-1 text-xs text-gray-500 shadow-sm">
              Loading more...
            </span>
          </div>
        )}
        {queryEnabled && hasNextPage && !isInitialLoading && (
          <div ref={setLoadMoreTriggerRef} className="h-px w-full" />
        )}
      </div>
    </div>
  );
};

export default FeedbackGalleryView;
